import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { toast } from "sonner";
import { AlertCircle, CreditCard, Banknote, Loader2, Star, Gift, MapPin, Clock, Check, Ticket, X, Building2 } from "lucide-react";
import { isCapServito } from "@/data/capServiti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Punti necessari per prodotto omaggio
const POINTS_FOR_FREE_PRODUCT = 250;

// Tutte le fasce orarie disponibili
const ALL_TIME_SLOTS = [
  "10:00-12:00", "12:00-14:00", "14:00-16:00", "16:00-18:00", "18:00-20:00"
];

// Mappa la zona DB ("A", "B", "UC") alla zona logica usata dal backend
const mapDbZoneToLogical = (dbZone) => {
  if (dbZone === "UC") return "UMBRIA_CONCORDARE";
  return dbZone || null;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, clearCart, getCartTotal } = useCart();
  const { customer, isLoggedIn, getToken, refreshCustomer } = useCustomer();
  const [cartCalculation, setCartCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("sumup");
  const [usePoints, setUsePoints] = useState(false);
  const [freeProduct, setFreeProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [selectedComune, setSelectedComune] = useState("");
  const [customCity, setCustomCity] = useState(""); // Città personalizzata per spedizione nazionale
  const [customCap, setCustomCap] = useState(""); // CAP per spedizione nazionale
  const [capValid, setCapValid] = useState(null); // null = non verificato, true = valido, false = non servito
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [slotsDetail, setSlotsDetail] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [birthdayDiscount, setBirthdayDiscount] = useState(null);
  const [useBirthdayDiscount, setUseBirthdayDiscount] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [hasGastronomia, setHasGastronomia] = useState(false);
  const [deliveryMode, setDeliveryMode] = useState("scheduled"); // "scheduled" o "asap" (prima possibile)
  const [dateBlocked, setDateBlocked] = useState(false);
  const [dateBlockReason, setDateBlockReason] = useState("");

  // Azienda partner
  const [partnerCompany, setPartnerCompany] = useState(null);
  const [deliveryType, setDeliveryType] = useState(null); // "aziendale" o "personale"
  
  // Honeypot anti-bot (campo nascosto)
  const [honeypot, setHoneypot] = useState("");
  const [deliveryComuni, setDeliveryComuni] = useState([]);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    customer_address: "",
    customer_fiscal_code: "",
    delivery_notes: "",
    delivery_date: "",
    delivery_time: "",
  });

  // Tipo account: affiliati partner hanno interfaccia privata
  const isPartnerAffiliated = !!customer?.partner_company_id;
  const isBusiness = isLoggedIn && customer?.account_type === "business" && !isPartnerAffiliated;
  const isPrivate = !isBusiness;
  // Qualunque account aziendale (anche se affiliato a un'azienda partner): consegna in sede
  const isBusinessAccount = isLoggedIn && customer?.account_type === "business";


  // Carica comuni dal DB
  useEffect(() => {
    axios.get(`${API}/delivery-comuni`).then(res => setDeliveryComuni(res.data || [])).catch(() => {});
  }, []);

  const ALL_COMUNI = deliveryComuni.filter(c => c.type === "diretto").map(c => c.name);
  const ZONE_UMBRIA_CONCORDARE = deliveryComuni.filter(c => c.type === "concordare").map(c => c.name);

  // Risolve la zona logica a partire dal nome del comune usando i dati dal DB
  const getZoneForComune = (comune) => {
    if (!comune) return null;
    if (comune === "ALTRA_CITTA") return "NAZIONALE";
    if (comune === "Sede aziendale") return "A"; // Aziende B2B: trattate come consegna locale
    const found = deliveryComuni.find(c => c.name === comune);
    if (!found) return null;
    return mapDbZoneToLogical(found.zone);
  };

  // Carica prodotti per selezione omaggio
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data.filter(p => !p.sold_out && p.stock > 0));
      } catch (error) {
        console.error("Errore caricamento prodotti:", error);
      }
    };
    fetchProducts();
  }, []);

  // Check birthday discount (solo per privati)
  useEffect(() => {
    const checkBirthdayDiscount = async () => {
      if (isLoggedIn && isPrivate) {
        try {
          const token = getToken();
          const response = await axios.get(`${API}/customer/birthday-discount`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.has_birthday_discount) {
            setBirthdayDiscount(response.data);
          }
        } catch (error) {
          console.error("Errore check compleanno:", error);
        }
      }
    };
    checkBirthdayDiscount();
  }, [isLoggedIn, customer]);

  // Fetch partner company if customer has one
  useEffect(() => {
    if (isLoggedIn && customer?.partner_company_id) {
      axios.get(`${API}/partner-companies`).then(res => {
        const company = res.data.find(c => c.id === customer.partner_company_id);
        if (company) {
          setPartnerCompany(company);
          setDeliveryType("aziendale");
        }
      }).catch(() => {});
    }
  }, [isLoggedIn, customer]);

  // Se passa a consegna aziendale, rimuovi coupon (non cumulabili)
  useEffect(() => {
    if (deliveryType === "aziendale" && partnerCompany && couponData) {
      setCouponCode("");
      setCouponData(null);
      setCouponError("");
    }
  }, [deliveryType]);

  // Pre-fill form with customer data if logged in
  useEffect(() => {
    if (isLoggedIn && customer) {
      // Per aziende: usa il CF aziendale (business_data.fiscal_code) se presente
      const isBiz = customer.account_type === "business";
      const cf = isBiz
        ? (customer.business_data?.fiscal_code || customer.fiscal_code || "")
        : (customer.fiscal_code || "");
      // Per aziende: usa l'indirizzo aziendale registrato come indirizzo di consegna
      const addr = isBiz
        ? (customer.business_data?.company_address || customer.address || "")
        : (customer.address || "");
      setFormData(prev => ({
        ...prev,
        customer_name: customer.name || "",
        customer_phone: customer.phone || "",
        customer_email: customer.email || "",
        customer_address: addr,
        customer_fiscal_code: cf
      }));
      // Per aziende (incluse affiliate partner): consegna sempre in sede, niente scelta comune
      if (isBiz) {
        setSelectedComune("Sede aziendale");
      }
    }
  }, [isLoggedIn, customer]);

  useEffect(() => {
    if (cart.length === 0) {
      navigate("/carrello");
      return;
    }
    // Controlla se ci sono prodotti gastronomia (business_only) nel carrello
    const hasGastro = cart.some(item => item.business_only === true);
    setHasGastronomia(hasGastro);
    // Se consegna aziendale selezionata ma nessuna monoporzione, forza "personale"
    const hasMono = cart.some(item => !item.business_only);
    if (deliveryType === "aziendale" && !hasMono) {
      setDeliveryType("personale");
    }
    calculateCart();
  }, [cart, selectedComune]); // Ricalcola quando cambia il comune (per spedizione nazionale)

  // Fetch available time slots when date or comune changes
  useEffect(() => {
    if (selectedComune && formData.delivery_date) {
      fetchAvailableSlots();
    } else {
      setAvailableTimeSlots([]);
    }
    setFormData(prev => ({ ...prev, delivery_time: "" }));
  }, [selectedComune, formData.delivery_date]);

  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    setDateBlocked(false);
    setDateBlockReason("");
    try {
      const zone = getZoneForComune(selectedComune);
      const response = await axios.get(`${API}/delivery/available-slots`, {
        params: {
          date: formData.delivery_date,
          zone: zone,
          comune: selectedComune
        }
      });
      
      // Controlla se il giorno è bloccato
      if (response.data.date_blocked) {
        setDateBlocked(true);
        setDateBlockReason(response.data.block_reason || "Nessuna consegna prevista");
        setAvailableTimeSlots([]);
        setSlotsDetail([]);
      } else {
        setAvailableTimeSlots(response.data.available_slots);
        setSlotsDetail(response.data.slots_detail || []);
      }
    } catch (error) {
      console.error("Errore caricamento fasce orarie:", error);
      setAvailableTimeSlots(ALL_TIME_SLOTS);
      setSlotsDetail([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const calculateCart = async () => {
    try {
      const items = cart.map((item) => ({
        id: item.id,
        product_id: item.id,
        quantity: item.quantity,
        name: item.name,
        price: item.price,
        business_only: item.business_only || false,
      }));

      const isNationalShipping = selectedComune === "ALTRA_CITTA";
      const isUmbriaConcordare = ZONE_UMBRIA_CONCORDARE.includes(selectedComune);
      const response = await axios.post(`${API}/cart/calculate`, { 
        items,
        is_national_shipping: isNationalShipping,
        is_umbria_concordare: isUmbriaConcordare
      });
      setCartCalculation(response.data);

      // Auto-attiva sconto compleanno se disponibile e ordine >= 50€
      if (birthdayDiscount?.has_birthday_discount && response.data.subtotal >= 50) {
        setUseBirthdayDiscount(true);
      }

      // TEST_MODE disabilita controllo minimo
      const TEST_MODE = false; // PRODUZIONE: controllo minimo attivo
      
      // Minimo diverso per aziende - si basa su can_checkout calcolato dal backend
      // Il controllo è sul totale FINALE dopo gli sconti
      const minOrder = isBusiness ? 50 : 30;
      if (!TEST_MODE && !response.data.can_checkout) {
        toast.error(`Il totale dopo gli sconti deve essere almeno €${minOrder}`);
        navigate("/carrello");
      }
    } catch (error) {
      console.error("Errore nel calcolo del carrello:", error);
      toast.error("Errore nel calcolo del carrello");
    }
  };

  // Check if customer can get free product (100 points) - solo privati
  const canGetFreeProduct = isLoggedIn && customer && isPrivate && (customer.points || 0) >= POINTS_FOR_FREE_PRODUCT;
  
  // Calcola subtotale monoporzione e gastronomia (per sconti)
  const subtotalMono = cart.filter(item => !item.business_only).reduce((sum, item) => sum + item.price * item.quantity, 0);
  const subtotalGastro = cart.filter(item => item.business_only).reduce((sum, item) => sum + item.price * item.quantity, 0);
  const hasMonoItems = cart.some(item => !item.business_only);
  
  // REGOLA SCONTI:
  // - Sconto aziendale e codice sconto NON cumulabili tra loro
  // - Sconto pagamento anticipato (1%) è l'UNICO cumulabile con tutto
  const hasCoupon = !!couponData;
  const isCompanyDeliveryActive = deliveryType === "aziendale" && partnerCompany;
  
  // Subtotale effettivo: per consegna aziendale usa SOLO monoporzioni
  const effectiveSubtotal = isCompanyDeliveryActive ? subtotalMono : (cartCalculation?.subtotal || 0);
  
  // Lo sconto volume viene annullato se c'è un codice sconto, compleanno o aziendale attivo
  // E si applica solo alla parte monoporzione
  const volumeDiscount = (hasCoupon || isCompanyDeliveryActive) ? 0 : (cartCalculation?.discount_amount || 0);
  
  // Partner company discount (solo per ordini aziendali, calcolato su monoporzioni, NON cumulabile con coupon)
  const partnerDiscountAmount = (isCompanyDeliveryActive && partnerCompany.discount_percentage > 0 && !hasCoupon)
    ? subtotalMono * (partnerCompany.discount_percentage / 100)
    : 0;
  
  // Coupon discount (NON cumulabile con sconto aziendale)
  const couponDiscountAmount = (hasCoupon && !isCompanyDeliveryActive) ? couponData.discount_amount : 0;
  
  // Calculate birthday discount (solo privati, solo su monoporzione, CUMULABILE con altri sconti)
  const canUseBirthdayDiscount = birthdayDiscount?.has_birthday_discount && effectiveSubtotal >= 50 && isPrivate;
  const birthdayDiscountAmount = useBirthdayDiscount && canUseBirthdayDiscount 
    ? subtotalMono * 0.05 
    : 0;
  
  // Sconto pagamento anticipato aziende B2B (5% mono + 2.5% gastro) - solo NON affiliate
  const businessPaymentDiscount = isBusiness && paymentMethod === "sumup"
    ? (subtotalMono * 0.05) + (isCompanyDeliveryActive ? 0 : subtotalGastro * 0.025)
    : 0;
  
  // Spedizione: gratuita per consegna aziendale o coupon spedizione gratuita
  const hasFreeShippingCoupon = hasCoupon && couponData?.free_shipping;
  const shippingCost = isCompanyDeliveryActive || hasFreeShippingCoupon ? 0 : (cartCalculation?.shipping_cost || 0);
  
  const baseAfterMainDiscount = Math.max(0, effectiveSubtotal - volumeDiscount - birthdayDiscountAmount - couponDiscountAmount - partnerDiscountAmount) + shippingCost;
  const privatePaymentDiscount = (isPrivate || isPartnerAffiliated) && paymentMethod === "sumup"
    ? baseAfterMainDiscount * 0.01
    : 0;

  // Totale prodotti dopo sconti (senza spedizione)
  const productsAfterDiscounts = Math.max(0, effectiveSubtotal - volumeDiscount - birthdayDiscountAmount - couponDiscountAmount - businessPaymentDiscount - privatePaymentDiscount - partnerDiscountAmount);
  
  // Calcola IVA al 10%
  // Per PRIVATI: i prezzi sono già IVA inclusa, quindi scorporiamo l'IVA (solo dai prodotti, non dalla spedizione)
  // Per AZIENDE: i prezzi sono senza IVA, quindi aggiungiamo l'IVA
  const ivaAmount = isBusiness 
    ? productsAfterDiscounts * 0.10  // Aziende: IVA da aggiungere
    : productsAfterDiscounts - (productsAfterDiscounts / 1.10);  // Privati: IVA già inclusa (scorporata)
  
  // Final total = prodotti dopo sconti + spedizione
  const finalTotal = isBusiness 
    ? productsAfterDiscounts + ivaAmount + shippingCost  // Aziende: totale + IVA + spedizione
    : productsAfterDiscounts + shippingCost;  // Privati: già IVA inclusa + spedizione

  const handleFreeProductToggle = () => {
    if (usePoints) {
      setUsePoints(false);
      setFreeProduct(null);
      setShowProductSelector(false);
    } else {
      setShowProductSelector(true);
    }
  };

  const selectFreeProduct = (product) => {
    setFreeProduct(product);
    setUsePoints(true);
    setShowProductSelector(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError("Inserisci un codice sconto");
      return;
    }
    
    setCouponLoading(true);
    setCouponError("");
    
    try {
      const response = await axios.post(`${API}/discount-codes/validate`, {
        code: couponCode.toUpperCase(),
        subtotal: cartCalculation?.subtotal || 0
      });
      setCouponData(response.data);
      toast.success(`Codice "${response.data.code}" applicato!`);
    } catch (error) {
      setCouponData(null);
      const msg = error.response?.data?.detail || "Codice non valido";
      setCouponError(msg);
      toast.error(msg);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode("");
    setCouponData(null);
    setCouponError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Anti-bot: se il campo honeypot è compilato, è un bot
    if (honeypot) {
      toast.error("Si è verificato un errore. Riprova.");
      return;
    }

    // Validazione form
    const isScheduled = deliveryMode === "scheduled";
    const isNationalShipping = selectedComune === "ALTRA_CITTA";
    const isUmbriaConcordare = ZONE_UMBRIA_CONCORDARE.includes(selectedComune);
    const isLocalDelivery = !isNationalShipping && !isUmbriaConcordare;
    
    // Validazione: se utente ha azienda partner, deve scegliere tipo consegna
    if (partnerCompany && !deliveryType) {
      toast.error("Scegli la modalità di consegna");
      return;
    }

    const isCompanyDelivery = deliveryType === "aziendale" && partnerCompany;

    // Blocca ordine aziendale se non ci sono monoporzioni
    if (isCompanyDelivery && !hasMonoItems) {
      toast.error("La consegna aziendale richiede almeno un prodotto monoporzione nel carrello");
      return;
    }

    // CF obbligatorio per privati e guest, NON per aziende (qualunque tipologia)
    const requiresFiscalCode = !isLoggedIn || (customer?.account_type !== "business");

    // Per consegna aziendale: non servono zona, data e orario manuali
    if (!isCompanyDelivery) {
      // Per aziende B2B (non affiliate): selectedComune è auto-impostato a "Sede aziendale",
      // quindi non blocca la validazione. Indirizzo richiesto.
      if (!formData.customer_name || !formData.customer_phone || !formData.customer_email || !formData.customer_address || !selectedComune) {
        toast.error("Compila tutti i campi obbligatori");
        return;
      }

      // Validazione Codice Fiscale per privati e guest
      if (requiresFiscalCode && !formData.customer_fiscal_code) {
        toast.error("Il Codice Fiscale è obbligatorio");
        return;
      }
    } else {
      if (!formData.customer_name || !formData.customer_phone || !formData.customer_email) {
        toast.error("Compila nome, telefono e email");
        return;
      }
      if (requiresFiscalCode && !formData.customer_fiscal_code) {
        toast.error("Il Codice Fiscale è obbligatorio");
        return;
      }
    }
    
    // Per spedizione nazionale, richiedi città e CAP (non per aziendale)
    if (!isCompanyDelivery && isNationalShipping && !customCity.trim()) {
      toast.error("Inserisci la tua città");
      return;
    }
    if (!isCompanyDelivery && isNationalShipping && (!customCap.trim() || customCap.length !== 5)) {
      toast.error("Inserisci un CAP valido (5 cifre)");
      return;
    }
    if (!isCompanyDelivery && isNationalShipping && !isCapServito(customCap)) {
      toast.error("Spiacenti, il CAP inserito non è servito dal nostro corriere");
      return;
    }
    
    // Per consegna locale schedulata, richiedi data e ora (non per aziendale)
    if (!isCompanyDelivery && isLocalDelivery && isScheduled && (!formData.delivery_date || !formData.delivery_time)) {
      toast.error("Seleziona data e ora di consegna");
      return;
    }

    setLoading(true);

    try {
      // Determina il comune da usare
      const finalComune = isCompanyDelivery ? partnerCompany.name : (isNationalShipping ? `${customCity} (${customCap})` : selectedComune);
      
      // Determina la modalità di consegna
      let deliveryModeValue = deliveryMode;
      let deliveryDateValue = formData.delivery_date;
      let deliveryTimeValue = formData.delivery_time;
      
      if (isCompanyDelivery) {
        deliveryModeValue = "aziendale";
        deliveryDateValue = partnerCompany.delivery_day;
        deliveryTimeValue = partnerCompany.delivery_time_slot;
      } else if (isNationalShipping) {
        deliveryModeValue = "nazionale";
        deliveryDateValue = "Da concordare";
        deliveryTimeValue = "Corriere BRT Fresh";
      } else if (isUmbriaConcordare) {
        deliveryModeValue = "concordare";
        deliveryDateValue = "Da concordare";
        deliveryTimeValue = "Da concordare";
      } else if (deliveryMode === "asap") {
        deliveryDateValue = "Prima possibile";
        deliveryTimeValue = "Da concordare";
      }

      // Per ordini aziendali, filtra solo monoporzioni
      const orderItems = isCompanyDelivery
        ? cart.filter(item => !item.business_only).map(item => ({ id: item.id, product_id: item.id, name: item.name, quantity: item.quantity, price: item.price }))
        : cart.map(item => ({ id: item.id, product_id: item.id, name: item.name, quantity: item.quantity, price: item.price }));
      
      const orderData = {
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        customer_email: formData.customer_email,
        customer_fiscal_code: formData.customer_fiscal_code,
        customer_address: isCompanyDelivery ? `c/o ${partnerCompany.name}` : `${formData.customer_address}, ${customCap ? customCap + ' ' : ''}${isNationalShipping ? customCity : selectedComune}`,
        delivery_comune: finalComune,
        delivery_zone: isCompanyDelivery ? "Aziendale" : getZoneForComune(selectedComune),
        delivery_cap: isNationalShipping ? customCap : null,
        delivery_notes: formData.delivery_notes,
        delivery_mode: deliveryModeValue,
        delivery_date: deliveryDateValue,
        delivery_time: deliveryTimeValue,
        is_national_shipping: isNationalShipping,
        is_umbria_concordare: isUmbriaConcordare,
        items: orderItems,
      };

      const response = await axios.post(`${API}/payments/checkout`, {
        order_data: orderData,
        origin_url: window.location.origin,
        payment_method: paymentMethod,
        customer_token: isLoggedIn ? getToken() : null,
        free_product: usePoints && freeProduct ? {
          id: freeProduct.id,
          name: freeProduct.name,
          price: freeProduct.price
        } : null,
        birthday_discount: useBirthdayDiscount && canUseBirthdayDiscount,
        coupon_code: couponData ? couponData.code : null,
        delivery_type: deliveryType || "personale",
        partner_company_id: deliveryType === "aziendale" && partnerCompany ? partnerCompany.id : null,
        is_national_shipping: isNationalShipping,
        is_umbria_concordare: isUmbriaConcordare
      });

      if (response.data.success) {
        if (paymentMethod === "cash") {
          const orderId = response.data.order_id;
          
          // Salva ordine in localStorage come backup
          localStorage.setItem(`order_${orderId}`, JSON.stringify(response.data));
          
          clearCart();
          
          if (isLoggedIn) {
            await refreshCustomer();
          }
          
          if (response.data.free_product_name) {
            toast.success(`Ordine confermato! Hai ricevuto "${response.data.free_product_name}" in omaggio!`);
          } else if (response.data.points_earned > 0) {
            toast.success(`Ordine confermato! Hai guadagnato ${response.data.points_earned} punti!`);
          } else {
            toast.success("Ordine confermato con successo!");
          }
          
          // Usa setTimeout per assicurarsi che la navigazione avvenga
          setTimeout(() => {
            navigate(`/conferma-ordine/${orderId}`);
          }, 100);
        } else if (paymentMethod === "sumup") {
          // Verifica che checkout_url esista e che checkout_id sia valido
          const checkoutUrl = response.data.checkout_url;
          const checkoutId = response.data.checkout_id;
          
          
          if (!checkoutUrl || !checkoutId) {
            console.error("SumUp checkout data missing:", response.data);
            toast.error("Errore creazione pagamento. Riprova.");
            return;
          }
          
          // Redirect a SumUp Checkout
          toast.info("Reindirizzamento a SumUp...");
          window.location.href = checkoutUrl;
        }
      } else {
        console.error("Response not successful:", response.data);
        toast.error("Errore nella creazione dell'ordine. Riprova.");
      }
    } catch (error) {
      console.error("Errore nell'invio dell'ordine:", error);
      const errorMsg = error.response?.data?.detail || "Errore nell'invio dell'ordine. Riprova.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 max-w-5xl mx-auto px-4 py-8 w-full" data-testid="checkout-page">
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-8" data-testid="checkout-title">Checkout</h1>

        {/* Avviso per prodotti Gastronomia */}
        {hasGastronomia && (
          <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-4 mb-6">
            <p className="text-yellow-500 font-semibold text-center">
              ⚠️ Il carrello contiene formati gastronomia: sconti e codici promozionali non applicabili
            </p>
          </div>
        )}

        {/* Coupon Code Section - NON per gastronomia, NON con sconto aziendale */}
        {!hasGastronomia && (
          <div className="bg-zinc-900 border border-green-500/50 rounded-lg p-4 mb-6" data-testid="coupon-section">
            <div className="flex items-center gap-3 mb-3">
              <Ticket className="w-6 h-6 text-green-500" />
              <h3 className="text-white font-semibold">Hai un codice sconto?</h3>
            </div>
            
            {isCompanyDeliveryActive ? (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-400 text-sm">
                  Il codice sconto non è cumulabile con lo sconto aziendale
                </p>
              </div>
            ) : couponData ? (
              <div className="bg-green-500/10 border border-green-500 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-white font-semibold">{couponData.code}</p>
                    <p className="text-green-500 text-sm">
                      {couponData.free_shipping 
                        ? "Spedizione gratuita"
                        : couponData.discount_type === "percentage" 
                          ? `${couponData.discount_value}% di sconto - Risparmi €${couponData.discount_amount.toFixed(2)}`
                          : `€${couponData.discount_value.toFixed(2)} di sconto - Risparmi €${couponData.discount_amount.toFixed(2)}`}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeCoupon}
                className="p-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-colors"
                data-testid="remove-coupon-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="Inserisci codice sconto"
                disabled={false}
                className="flex-1 bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white uppercase focus:outline-none focus:border-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="coupon-input"
              />
              <button
                type="button"
                onClick={validateCoupon}
                disabled={couponLoading || !couponCode.trim()}
                className="bg-green-500 text-black px-4 py-3 rounded-lg font-semibold hover:bg-green-400 disabled:bg-zinc-700 disabled:text-zinc-500 transition-colors flex items-center gap-2"
                data-testid="apply-coupon-btn"
              >
                {couponLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Applica"
                )}
              </button>
            </div>
          )}
          
          {couponError && (
            <p className="text-red-500 text-sm mt-2">{couponError}</p>
          )}
          </div>
        )}

        {/* Points Banner - Solo per privati */}
        {!isLoggedIn && (
          <div className="bg-gradient-to-r from-green-600/20 to-green-500/20 border border-green-500 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <Gift className="w-8 h-8 text-green-500" />
              <div className="flex-1">
                <p className="text-white font-semibold">Accumula punti con questo ordine!</p>
                <p className="text-zinc-400 text-sm">
                  <Link to="/accedi" className="text-green-500 hover:underline">Accedi</Link> o{" "}
                  <Link to="/accedi" className="text-green-500 hover:underline">registrati</Link> per guadagnare{" "}
                  <span className="text-green-500 font-bold">{Math.floor(cartCalculation?.total || 0)} punti</span> con questo ordine!
                </p>
              </div>
            </div>
          </div>
        )}

        {isLoggedIn && isPrivate && (
          <div className="bg-green-500/10 border border-green-500 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <Star className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-white font-semibold">
                  Guadagnerai <span className="text-green-500">{Math.floor(finalTotal)} punti</span> con questo ordine!
                </p>
                <p className="text-zinc-400 text-sm">
                  I tuoi punti attuali: <span className="text-green-500 font-bold">{customer?.points || 0}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Free Product Section - 100 points = 1 free product */}
        {isLoggedIn && canGetFreeProduct && (
          <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Gift className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-white font-semibold">Prodotto Omaggio!</p>
                  <p className="text-zinc-400 text-sm">
                    Hai <span className="text-yellow-500 font-bold">{customer?.points}</span> punti - Scegli un prodotto gratis!
                  </p>
                </div>
              </div>
              {!usePoints ? (
                <button
                  type="button"
                  onClick={handleFreeProductToggle}
                  className="bg-yellow-500 text-black font-bold px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors"
                >
                  Scegli omaggio
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleFreeProductToggle}
                  className="bg-zinc-700 text-white font-bold px-4 py-2 rounded-lg hover:bg-zinc-600 transition-colors"
                >
                  Rimuovi
                </button>
              )}
            </div>
            
            {/* Selected free product */}
            {usePoints && freeProduct && (
              <div className="mt-4 pt-4 border-t border-yellow-500/30">
                <div className="flex items-center justify-between bg-yellow-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="text-white font-semibold">{freeProduct.name}</p>
                      <p className="text-yellow-500 text-sm">OMAGGIO (valore €{freeProduct.price.toFixed(2)})</p>
                    </div>
                  </div>
                  <span className="text-yellow-500 font-bold">-250 punti</span>
                </div>
              </div>
            )}

            {/* Product selector modal */}
            {showProductSelector && (
              <div className="mt-4 pt-4 border-t border-yellow-500/30">
                <p className="text-white font-semibold mb-3">Scegli il tuo prodotto omaggio:</p>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => selectFreeProduct(product)}
                      className="w-full text-left bg-zinc-800 hover:bg-zinc-700 rounded-lg p-3 transition-colors flex items-center justify-between"
                    >
                      <div>
                        <p className="text-white font-medium">{product.name}</p>
                        <p className="text-zinc-400 text-sm">{product.category}</p>
                      </div>
                      <span className="text-green-500 font-bold">€{product.price.toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Birthday Discount Section */}
        {canUseBirthdayDiscount && (
          <div className="bg-pink-500/10 border border-pink-500 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🎂</span>
                <div>
                  <p className="text-white font-semibold">Buon Compleanno!</p>
                  <p className="text-zinc-400 text-sm">
                    Hai diritto al <span className="text-pink-500 font-bold">5% di sconto</span> sul tuo ordine!
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white text-sm">Applica</span>
                <div 
                  onClick={() => !hasCoupon && setUseBirthdayDiscount(!useBirthdayDiscount)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    hasCoupon ? 'bg-zinc-800 cursor-not-allowed opacity-50' : 
                    useBirthdayDiscount ? 'bg-pink-500 cursor-pointer' : 'bg-zinc-700 cursor-pointer'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${useBirthdayDiscount && !hasCoupon ? 'left-7' : 'left-1'}`}></div>
                </div>
              </div>
            </div>
            {hasCoupon && (
              <p className="text-zinc-500 text-xs mt-2">Non cumulabile con il codice sconto applicato</p>
            )}
            {useBirthdayDiscount && !hasCoupon && (
              <div className="mt-3 pt-3 border-t border-pink-500/30 flex justify-between items-center">
                <span className="text-zinc-300">Sconto compleanno (10%)</span>
                <span className="text-pink-500 font-bold">-€{birthdayDiscountAmount.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        {/* Card payment bonus info - diverso per privati e aziende */}
        {isLoggedIn && isPrivate && (
          <div className="bg-blue-500/10 border border-blue-500/50 rounded-lg p-3 mb-6">
            <p className="text-blue-400 text-sm text-center">
              💳 <strong>+5 punti bonus</strong> se paghi con carta!
            </p>
          </div>
        )}

        {/* Sconto pagamento anticipato per aziende */}
        {isLoggedIn && isBusiness && (
          <div className="bg-green-500/10 border border-green-500 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2">
              <span className="text-2xl">💳</span>
              <div className="text-center">
                <p className="text-green-500 font-semibold">
                  Paga subito con carta e risparmia!
                </p>
                <p className="text-green-400 text-sm">
                  {isPartnerAffiliated 
                    ? "1% di sconto sul totale" 
                    : "5% sui monoporzione • 2,5% sui formati gastronomia"}
                </p>
              </div>
            </div>
          </div>
        )}

        {isLoggedIn && !canGetFreeProduct && customer?.points > 0 && isPrivate && (
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <Gift className="w-6 h-6 text-zinc-500" />
              <p className="text-zinc-400 text-sm">
                Hai {customer?.points} punti. Raggiungi {POINTS_FOR_FREE_PRODUCT} punti per ottenere un prodotto omaggio!
              </p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="checkout-form">
              {/* Partner Company Delivery Type Selector */}
              {partnerCompany && (
                <div className="bg-zinc-900 border border-blue-500/50 rounded-lg p-6" data-testid="delivery-type-selector">
                  <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                    <Building2 className="w-6 h-6 text-blue-500" />
                    Modalità di consegna
                  </h2>
                  <p className="text-zinc-400 text-sm mb-4">
                    Sei associato a <strong className="text-blue-400">{partnerCompany.name}</strong>. Come vuoi ricevere il tuo ordine?
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => hasMonoItems && setDeliveryType("aziendale")}
                      disabled={!hasMonoItems}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        !hasMonoItems
                          ? "border-zinc-700 bg-zinc-800/50 opacity-50 cursor-not-allowed"
                          : deliveryType === "aziendale"
                            ? "border-blue-500 bg-blue-500/10"
                            : "border-zinc-700 hover:border-zinc-600"
                      }`}
                      data-testid="delivery-type-aziendale"
                    >
                      <p className="text-white font-bold mb-1">Consegna in azienda</p>
                      <p className="text-zinc-400 text-sm">{partnerCompany.delivery_day}, {partnerCompany.delivery_time_slot}</p>
                      {partnerCompany.discount_percentage > 0 && (
                        <p className="text-green-500 text-sm mt-1 font-semibold">Sconto {partnerCompany.discount_percentage}% incluso</p>
                      )}
                      <p className="text-yellow-500 text-xs mt-1">Solo monoporzioni</p>
                      {!hasMonoItems && (
                        <p className="text-red-400 text-xs mt-1">Non disponibile: il carrello non contiene monoporzioni</p>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDeliveryType("personale");
                        setDeliveryMode("scheduled");
                      }}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        deliveryType === "personale" && deliveryMode === "scheduled"
                          ? "border-green-500 bg-green-500/10"
                          : "border-zinc-700 hover:border-zinc-600"
                      }`}
                      data-testid="delivery-type-personale"
                    >
                      <p className="text-white font-bold mb-1">Consegna personale</p>
                      <p className="text-zinc-400 text-sm">Scegli data, orario e indirizzo</p>
                      <p className="text-zinc-500 text-xs mt-1">Tutti i prodotti disponibili</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDeliveryType("personale");
                        setDeliveryMode("asap");
                        setFormData(prev => ({ ...prev, delivery_date: "", delivery_time: "" }));
                      }}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        deliveryType === "personale" && deliveryMode === "asap"
                          ? "border-yellow-500 bg-yellow-500/10"
                          : "border-zinc-700 hover:border-zinc-600"
                      }`}
                      data-testid="delivery-type-asap"
                    >
                      <p className="text-white font-bold mb-1">Prima possibile</p>
                      <p className="text-zinc-400 text-sm">Ti contatteremo per concordare data e orario</p>
                      <p className="text-zinc-500 text-xs mt-1">Tutti i prodotti disponibili</p>
                    </button>
                  </div>
                  {deliveryType === "aziendale" && cart.some(item => item.business_only) && (
                    <div className="mt-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                      <p className="text-yellow-400 text-sm">
                        Attenzione: i prodotti gastronomia nel carrello non saranno inclusi nell'ordine aziendale.
                      </p>
                    </div>
                  )}
                  <p className="text-zinc-500 text-xs mt-3" data-testid="partner-phone-hint">
                    Per assistenza chiamaci: {" "}
                    <a href="tel:+393342057548" className="text-zinc-300 hover:text-white underline">334 205 7548</a>
                    {" · "}
                    <a href="tel:+393928612924" className="text-zinc-300 hover:text-white underline">392 861 2924</a>
                  </p>
                </div>
              )}

              {/* Customer Info */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <h2 className="text-2xl font-bold text-white mb-4">Dati cliente</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-white font-semibold mb-2" htmlFor="customer_name">
                      Nome completo *
                    </label>
                    <input
                      type="text"
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                      required
                      data-testid="customer-name-input"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white font-semibold mb-2" htmlFor="customer_phone">
                        Telefono *
                      </label>
                      <input
                        type="tel"
                        id="customer_phone"
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleInputChange}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                        required
                        data-testid="customer-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-semibold mb-2" htmlFor="customer_email">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="customer_email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleInputChange}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                        required
                        data-testid="customer-email-input"
                      />
                    </div>
                  </div>
                  {/* Codice Fiscale - visibile per privati e non registrati (NON per aziende) */}
                  {(!isLoggedIn || customer?.account_type !== "business") && (
                    <div>
                      <label className="block text-white font-semibold mb-2" htmlFor="customer_fiscal_code">
                        Codice Fiscale *
                      </label>
                      <input
                        type="text"
                        id="customer_fiscal_code"
                        name="customer_fiscal_code"
                        value={formData.customer_fiscal_code}
                        onChange={(e) => setFormData({...formData, customer_fiscal_code: e.target.value.toUpperCase()})}
                        className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white uppercase focus:outline-none focus:border-green-500"
                        placeholder="RSSMRA85M01H501Z"
                        maxLength={16}
                        required
                        data-testid="customer-fiscal-code-input"
                      />
                      <p className="text-zinc-500 text-sm mt-1">Necessario per l'emissione della fattura</p>
                    </div>
                  )}
                  {!(deliveryType === "aziendale" && partnerCompany) && (
                  <div>
                    <label className="block text-white font-semibold mb-2" htmlFor="customer_address">
                      {isBusinessAccount ? "Indirizzo sede aziendale *" : "Indirizzo di consegna *"}
                    </label>
                    <input
                      type="text"
                      id="customer_address"
                      name="customer_address"
                      value={formData.customer_address}
                      onChange={handleInputChange}
                      className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                      placeholder="Via e numero civico"
                      required
                      data-testid="customer-address-input"
                    />
                    {isBusinessAccount && (
                      <p className="text-zinc-500 text-xs mt-1">
                        Pre-compilato dai dati aziendali. Modificalo se la consegna deve avvenire in un altro indirizzo.
                      </p>
                    )}
                  </div>
                  )}
                  
                  {/* Honeypot field - nascosto agli utenti, visibile ai bot */}
                  <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                    <label htmlFor="website_url">Website</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>
                  
                  {/* Banner informativo per aziende (anche affiliate): consegna sempre in sede */}
                  {isBusinessAccount && !(deliveryType === "aziendale" && partnerCompany) && (
                    <div className="bg-blue-500/10 border-2 border-blue-500 rounded-lg p-4 mb-2">
                      <p className="text-blue-400 font-semibold flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        Consegna in sede aziendale
                      </p>
                      <p className="text-zinc-300 text-sm mt-2">
                        La consegna avverrà presso la sede della tua azienda all'indirizzo indicato sopra.
                      </p>
                      <p className="text-zinc-400 text-xs mt-2">
                        Spedizione €5 — gratuita sopra €50 di ordine.
                      </p>
                    </div>
                  )}

                  {/* Zona di consegna - nascosta per consegna aziendale (partner) e aziende (consegna in sede) */}
                  {!(deliveryType === "aziendale" && partnerCompany) && !isBusinessAccount && (
                  <div>
                    <label className="block text-white font-semibold mb-3" htmlFor="delivery_comune">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Zona di consegna *
                    </label>
                    
                    {/* ZONA 1: Consegna diretta */}
                    <div className="mb-4 p-4 rounded-lg border-2 border-green-500 bg-green-500/10">
                      <p className="text-green-500 font-bold mb-2 flex items-center gap-2">
                        <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                        CONSEGNA DIRETTA
                      </p>
                      <p className="text-zinc-400 text-sm mb-3">Spedizione €5 (gratuita sopra €50)</p>
                      <div className="grid grid-cols-2 gap-2">
                        {ALL_COMUNI.map((comune) => (
                          <button
                            key={comune}
                            type="button"
                            onClick={() => {
                              setSelectedComune(comune);
                              setCustomCity("");
                              setCustomCap("");
                              setCapValid(null);
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              selectedComune === comune
                                ? "bg-green-500 text-black"
                                : "bg-zinc-800 text-white hover:bg-zinc-700"
                            }`}
                          >
                            {comune}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* ZONA 2: Altre zone Umbria */}
                    <div className="mb-4 p-4 rounded-lg border-2 border-yellow-500 bg-yellow-500/10">
                      <p className="text-yellow-500 font-bold mb-2 flex items-center gap-2">
                        <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                        ALTRE ZONE UMBRIA
                      </p>
                      <p className="text-zinc-400 text-sm mb-3">Spedizione €10 (gratuita sopra €50) - Data da concordare</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {ZONE_UMBRIA_CONCORDARE.map((comune) => (
                          <button
                            key={comune}
                            type="button"
                            onClick={() => {
                              setSelectedComune(comune);
                              setCustomCity("");
                              setCustomCap("");
                              setCapValid(null);
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              selectedComune === comune
                                ? "bg-yellow-500 text-black"
                                : "bg-zinc-800 text-white hover:bg-zinc-700"
                            }`}
                          >
                            {comune}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* ZONA 3: Spedizione nazionale - SOSPESA
                    <div className="p-4 rounded-lg border-2 border-zinc-700 bg-zinc-800/50 opacity-50">
                      <p className="text-zinc-500 font-bold mb-2 flex items-center gap-2">
                        <span className="w-3 h-3 bg-zinc-500 rounded-full"></span>
                        SPEDIZIONE NAZIONALE (Temporaneamente sospesa)
                      </p>
                      <p className="text-zinc-600 text-sm">Servizio non disponibile al momento</p>
                    </div>
                    */}
                    
                    {/* Hidden input for form */}
                    <input type="hidden" name="delivery_comune" value={selectedComune} required />
                    
                    {/* Info per zone Umbria da concordare */}
                    {ZONE_UMBRIA_CONCORDARE.includes(selectedComune) && (
                      <div className="mt-4 bg-yellow-500/20 border border-yellow-500 rounded-lg p-4">
                        <p className="text-yellow-400">
                          <strong>📞 Ti contatteremo</strong> per accordarci su data e orario di consegna.
                        </p>
                      </div>
                    )}
                    
                    {/* Campo città personalizzata per spedizione nazionale */}
                    {selectedComune === "ALTRA_CITTA" && (
                      <div className="mt-4 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-white font-semibold mb-2">
                              Città *
                            </label>
                            <input
                              type="text"
                              value={customCity}
                              onChange={(e) => setCustomCity(e.target.value)}
                              placeholder="Es. Roma, Milano..."
                              className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                              required
                              data-testid="custom-city-input"
                            />
                          </div>
                          <div>
                            <label className="block text-white font-semibold mb-2">
                              CAP *
                            </label>
                            <input
                              type="text"
                              value={customCap}
                              onChange={(e) => {
                                const cap = e.target.value.replace(/\D/g, '').slice(0, 5);
                                setCustomCap(cap);
                                if (cap.length === 5) {
                                  setCapValid(isCapServito(cap));
                                } else {
                                  setCapValid(null);
                                }
                              }}
                              placeholder="Es. 00100"
                              maxLength={5}
                              className={`w-full bg-black border rounded-lg px-4 py-3 text-white focus:outline-none ${
                                capValid === null ? 'border-zinc-700 focus:border-green-500' :
                                capValid ? 'border-green-500' : 'border-red-500'
                              }`}
                              required
                              data-testid="custom-cap-input"
                            />
                            {capValid === true && (
                              <p className="text-green-500 text-sm mt-1 flex items-center gap-1">
                                <Check className="w-4 h-4" /> CAP servito
                              </p>
                            )}
                            {capValid === false && (
                              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                <X className="w-4 h-4" /> CAP non servito dal corriere
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="bg-blue-500/10 border border-blue-500 rounded-lg p-3">
                          <p className="text-blue-400 text-sm">
                            <strong>Spedizione con corriere BRT Fresh</strong><br/>
                            • Sopra €100: <strong>GRATIS</strong><br/>
                            • Da €65 a €100: €4,00<br/>
                            • Da €50 a €65: €8,00<br/>
                            • Da €40 a €50: €15,00<br/>
                            • Ordine minimo: €40
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {selectedComune && selectedComune !== "ALTRA_CITTA" && !ZONE_UMBRIA_CONCORDARE.includes(selectedComune) && (
                      <p className="text-zinc-500 text-sm mt-1">
                        Consegna diretta nella tua zona
                      </p>
                    )}
                  </div>
                  )}
                </div>
              </div>

              {/* Delivery Info - Solo per consegna locale diretta (non aziendale) */}
              {!(deliveryType === "aziendale" && partnerCompany) && selectedComune && selectedComune !== "ALTRA_CITTA" && !ZONE_UMBRIA_CONCORDARE.includes(selectedComune) && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <h2 className="text-2xl font-bold text-white mb-4">Data e orario consegna</h2>
                
                {/* Toggle Modalità Consegna */}
                <div className="bg-zinc-800 rounded-lg p-4 mb-4">
                    <p className="text-white font-semibold mb-3">Quando vuoi ricevere l'ordine?</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setDeliveryMode("scheduled")}
                        className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                          deliveryMode === "scheduled" 
                            ? "bg-green-500 text-black" 
                            : "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                        }`}
                      >
                        📅 Scegli data e ora
                      </button>
                    </div>
                    
                    {/* Opzione secondaria "Prima possibile" */}
                    <div className="mt-3 text-center">
                      <button
                        type="button"
                        onClick={() => {
                          setDeliveryMode("asap");
                          setFormData(prev => ({ ...prev, delivery_date: "", delivery_time: "" }));
                        }}
                        className={`text-sm transition-all ${
                          deliveryMode === "asap" 
                            ? "text-yellow-500 underline" 
                            : "text-zinc-500 hover:text-zinc-300 underline"
                        }`}
                      >
                        oppure scegli "Prima possibile" (ti contatteremo)
                      </button>
                    </div>
                  </div>

                {/* Opzione "Prima possibile" */}
                {deliveryMode === "asap" && selectedComune && (
                  <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">📞</span>
                      <div>
                        <p className="text-yellow-500 font-semibold">Consegna "Prima possibile"</p>
                        <p className="text-zinc-300 text-sm mt-1">
                          Ti contatteremo telefonicamente al numero indicato per concordare insieme il giorno e l'orario di consegna più comodo per te.
                        </p>
                        <p className="text-zinc-400 text-sm mt-2">
                          Oppure chiamaci tu ai numeri:
                          {" "}
                          <a href="tel:+393342057548" className="text-yellow-400 font-semibold hover:underline" data-testid="phone-primary-link">334 205 7548</a>
                          {" · "}
                          <a href="tel:+393928612924" className="text-yellow-400 font-semibold hover:underline" data-testid="phone-secondary-link">392 861 2924</a>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Form data/ora standard */}
                {deliveryMode === "scheduled" && (
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-white font-semibold mb-2" htmlFor="delivery_date">
                          Data consegna *
                        </label>
                        <input
                          type="date"
                          id="delivery_date"
                          name="delivery_date"
                          value={formData.delivery_date}
                          onChange={handleInputChange}
                          min={getTomorrowDate()}
                          className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                          required={deliveryMode === "scheduled"}
                          data-testid="delivery-date-input"
                        />
                        <p className="text-zinc-500 text-sm mt-1">
                          La consegna è disponibile a partire da domani
                        </p>
                      </div>
                      <div>
                        <label className="block text-white font-semibold mb-2" htmlFor="delivery_time">
                          <Clock className="w-4 h-4 inline mr-1" />
                          Fascia oraria *
                        </label>
                        {loadingSlots ? (
                          <div className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-zinc-500 flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Caricamento fasce disponibili...
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {slotsDetail.length === 0 ? (
                              <div className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-zinc-500">
                                Seleziona prima data e comune
                              </div>
                            ) : (
                              slotsDetail.map((slotInfo) => (
                                <button
                                  key={slotInfo.slot}
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, delivery_time: slotInfo.slot }))}
                                  disabled={!selectedComune || !formData.delivery_date}
                                  className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                                    formData.delivery_time === slotInfo.slot
                                      ? "bg-green-500/20 border-green-500 text-white"
                                      : "bg-black border-zinc-700 text-zinc-300 hover:border-zinc-600"
                                  } disabled:opacity-50`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <span className="font-medium">{slotInfo.slot}</span>
                                      <span className="text-sm text-zinc-500">({slotInfo.remaining} posti)</span>
                                    </div>
                                    {slotInfo.bonus_points && (
                                      <span className="bg-yellow-500 text-black px-2 py-0.5 rounded-full text-xs font-bold">
                                        +10 PUNTI
                                      </span>
                                    )}
                                  </div>
                                  {slotInfo.bonus_points && (
                                    <p className="text-yellow-500 text-xs mt-1">
                                      Consegna ottimizzata: altri ordini nella tua zona!
                                    </p>
                                  )}
                                </button>
                              ))
                            )}
                            {/* Hidden input for form validation */}
                            <input
                              type="hidden"
                              name="delivery_time"
                              value={formData.delivery_time}
                              required={deliveryMode === "scheduled"}
                            />
                          </div>
                        )}
                        {dateBlocked && (
                          <div className="bg-red-500/10 border border-red-500 rounded-lg p-3 mt-2">
                            <p className="text-red-500 text-sm font-semibold">
                              ⚠️ Questo giorno non è disponibile per le consegne
                            </p>
                            <p className="text-red-400 text-xs mt-1">{dateBlockReason}</p>
                          </div>
                        )}
                        {selectedComune && formData.delivery_date && availableTimeSlots.length === 0 && !loadingSlots && !dateBlocked && (
                          <p className="text-red-500 text-sm mt-1">
                            Nessuna fascia oraria disponibile per questa data. Prova un altro giorno.
                          </p>
                        )}
                        {selectedComune && formData.delivery_date && availableTimeSlots.length > 0 && (
                          <p className="text-zinc-500 text-sm mt-1">
                            {availableTimeSlots.length} fasce disponibili per {selectedComune}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <label className="block text-white font-semibold mb-2" htmlFor="delivery_notes">
                    Note consegna (opzionale)
                  </label>
                  <textarea
                    id="delivery_notes"
                    name="delivery_notes"
                    value={formData.delivery_notes}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                    placeholder="Es. citofono, note per il corriere..."
                    data-testid="delivery-notes-input"
                  />
                </div>
              </div>
              )}

              {/* Note per spedizione nazionale o zone Umbria da concordare */}
              {(selectedComune === "ALTRA_CITTA" || ZONE_UMBRIA_CONCORDARE.includes(selectedComune)) && (
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">
                    {selectedComune === "ALTRA_CITTA" ? "Note per la spedizione" : "Note per la consegna"}
                  </h2>
                  <textarea
                    id="delivery_notes"
                    name="delivery_notes"
                    value={formData.delivery_notes}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                    placeholder={selectedComune === "ALTRA_CITTA" 
                      ? "Es. indicazioni per il corriere, orari preferiti..." 
                      : "Es. orari preferiti, indicazioni per trovarti..."
                    }
                    data-testid="delivery-notes-input"
                  />
                  <p className="text-zinc-500 text-sm mt-2">
                    Ti contatteremo per concordare data e orario di consegna.
                  </p>
                </div>
              )}

              {/* Payment Method Selection */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
                <h2 className="text-2xl font-bold text-white mb-4">Metodo di pagamento</h2>
                <div className="space-y-3">
                  {/* SumUp Payment */}
                  <label 
                    className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      paymentMethod === "sumup" 
                        ? "border-green-500 bg-green-500/10" 
                        : "border-zinc-700 bg-black hover:border-zinc-600"
                    }`}
                    data-testid="payment-sumup"
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="sumup"
                      checked={paymentMethod === "sumup"}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="hidden"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === "sumup" ? "border-green-500" : "border-zinc-600"
                    }`}>
                      {paymentMethod === "sumup" && (
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      )}
                    </div>
                    <CreditCard className={`w-6 h-6 ${paymentMethod === "sumup" ? "text-green-500" : "text-zinc-400"}`} />
                    <div className="flex-1">
                      <p className="text-white font-semibold">Paga con carta</p>
                      <p className="text-zinc-400 text-sm">Pagamento sicuro online con SumUp</p>
                    </div>
                  </label>

                  {/* Cash on Delivery - Non disponibile per spedizioni BRT */}
                  {selectedComune !== "ALTRA_CITTA" && (
                  <label 
                    className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      paymentMethod === "cash" 
                        ? "border-green-500 bg-green-500/10" 
                        : "border-zinc-700 bg-black hover:border-zinc-600"
                    }`}
                    data-testid="payment-cash"
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={paymentMethod === "cash"}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="hidden"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === "cash" ? "border-green-500" : "border-zinc-600"
                    }`}>
                      {paymentMethod === "cash" && (
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      )}
                    </div>
                    <Banknote className={`w-6 h-6 ${paymentMethod === "cash" ? "text-green-500" : "text-zinc-400"}`} />
                    <div className="flex-1">
                      <p className="text-white font-semibold">Pagamento alla consegna</p>
                      <p className="text-zinc-400 text-sm">Paga in contanti o con carta al momento della consegna</p>
                    </div>
                  </label>
                  )}
                  
                  {/* Avviso per BRT */}
                  {selectedComune === "ALTRA_CITTA" && (
                    <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3">
                      <p className="text-zinc-400 text-sm">
                        Per le spedizioni nazionali è disponibile solo il pagamento anticipato con carta.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Company delivery info */}
              {deliveryType === "aziendale" && partnerCompany && (
                <div className="bg-blue-500/10 border border-blue-500/50 rounded-lg p-4" data-testid="company-delivery-info">
                  <p className="text-blue-400 font-bold flex items-center gap-2 mb-2">
                    <Building2 className="w-5 h-5" />
                    Consegna aziendale - {partnerCompany.name}
                  </p>
                  <div className="text-zinc-300 text-sm space-y-1">
                    <p>Giorno: <strong className="text-white">{partnerCompany.delivery_day}</strong></p>
                    <p>Fascia oraria: <strong className="text-white">{partnerCompany.delivery_time_slot}</strong></p>
                    {partnerCompany.discount_percentage > 0 && (
                      <p>Sconto: <strong className="text-green-500">{partnerCompany.discount_percentage}%</strong></p>
                    )}
                  </div>
                </div>
              )}

              {/* Important Note */}
              <div className="bg-green-500/10 border border-green-500 rounded-lg p-4 flex gap-3" data-testid="refrigerated-note">
                <AlertCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                <p className="text-green-500 font-semibold">
                  I prodotti verranno consegnati refrigerati. Conservare in frigorifero.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-yellow-500 text-black py-4 rounded-lg font-bold text-lg hover:bg-yellow-600 transition-all disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="confirm-order-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Elaborazione...
                  </>
                ) : paymentMethod === "sumup" ? (
                  "Procedi al pagamento"
                ) : (
                  "Conferma ordine"
                )}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 sticky top-24" data-testid="order-summary">
              <h2 className="text-2xl font-bold text-white mb-6">Riepilogo ordine</h2>
              
              <div className="space-y-3 mb-6">
                {cart.map((item) => {
                  const excluded = deliveryType === "aziendale" && partnerCompany && item.business_only;
                  return (
                    <div key={item.id} className={`flex justify-between text-sm ${excluded ? 'text-zinc-600 line-through' : 'text-zinc-300'}`} data-testid={`summary-item-${item.id}`}>
                      <span>{item.name} x{item.quantity} {excluded && '(escluso)'}</span>
                      <span>€{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  );
                })}
                {deliveryType === "aziendale" && partnerCompany && hasGastronomia && (
                  <p className="text-yellow-500 text-xs mt-1">I prodotti gastronomia sono esclusi dalla consegna aziendale</p>
                )}
              </div>

              {cartCalculation && (
                <>
                  <div className="border-t border-zinc-700 pt-4 space-y-3">
                    <div className="flex justify-between text-zinc-300">
                      <span>Subtotale{isCompanyDeliveryActive ? " (solo monoporzioni)" : ""}</span>
                      <span data-testid="summary-subtotal">€{effectiveSubtotal.toFixed(2)}</span>
                    </div>
                    {volumeDiscount > 0 && (
                      <div className="flex justify-between text-green-500 font-semibold">
                        <span>Sconto volume ({cartCalculation.discount_percentage}%)</span>
                        <span data-testid="summary-discount">-€{volumeDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-zinc-300">
                      <span>Spedizione</span>
                      <span data-testid="summary-shipping" className={shippingCost === 0 ? "text-green-500" : ""}>
                        {shippingCost === 0 ? "Gratuita" : `€${shippingCost.toFixed(2)}`}
                      </span>
                    </div>
                    {couponData && (
                      <div className="flex justify-between text-green-500 font-semibold" data-testid="summary-coupon">
                        <span>Codice {couponData.code}</span>
                        <span>-€{couponData.discount_amount.toFixed(2)}</span>
                      </div>
                    )}
                    {useBirthdayDiscount && birthdayDiscountAmount > 0 && (
                      <div className="flex justify-between text-pink-500 font-semibold">
                        <span>Sconto Compleanno (5%)</span>
                        <span>-€{birthdayDiscountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {usePoints && freeProduct && (
                      <div className="flex justify-between text-yellow-500 font-semibold">
                        <span>Omaggio: {freeProduct.name}</span>
                        <span>GRATIS</span>
                      </div>
                    )}
                    {businessPaymentDiscount > 0 && (
                      <div className="flex justify-between text-green-500 font-semibold" data-testid="summary-business-discount">
                        <span>Sconto pagamento anticipato (5%)</span>
                        <span>-€{businessPaymentDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    {privatePaymentDiscount > 0 && (
                      <div className="flex justify-between text-green-500 font-semibold" data-testid="summary-private-discount">
                        <span>Sconto pagamento anticipato (1%)</span>
                        <span>-€{privatePaymentDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    {partnerDiscountAmount > 0 && (
                      <div className="flex justify-between text-blue-400 font-semibold" data-testid="summary-partner-discount">
                        <span>Sconto aziendale ({partnerCompany.discount_percentage}%)</span>
                        <span>-€{partnerDiscountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-zinc-300">
                      <span>{isBusiness ? "IVA (10%)" : "di cui IVA (10%)"}</span>
                      <span data-testid="summary-iva">{isBusiness ? "+" : ""}€{ivaAmount.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-zinc-700 pt-3 flex justify-between text-white text-xl font-bold">
                      <span>Totale {!isBusiness && <span className="text-sm font-normal text-zinc-400">(IVA inclusa)</span>}</span>
                      <span data-testid="summary-total">€{finalTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
